import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  styled,
  useTheme,
  useMediaQuery,
  Drawer,
  Button,
  Divider,
  Tooltip,
  Chip,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Logout as LogoutIcon,
  Assignment as AssignmentIcon,
  AccountTree as WorkflowIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  Hub as GraphIcon,
  MoreVert as MoreVertIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useChatStore, Conversation } from '../../stores/chatStore';
import { useAuthStore } from '../../store/authStore';
import API_CONFIG from '../../config/apiConfig';
import ChatSidebar from '../../components/Chat/ChatSidebar';
import MessageList from '../../components/Chat/MessageList';
import MessageInput from '../../components/Chat/MessageInput';
import SettingsDialog from '../../components/Settings/SettingsDialog';
import ChatLayout from '../../components/Layout/ChatLayout';
import { useHVACPanel } from '../../hooks/useHVACPanel';
import HVACCalculationPanel from '../../components/HVACCalculationPanel';
import TaskSuggestionsSplitView from '../../components/TaskSuggestions/TaskSuggestionsSplitView';
import { ModeSelector } from '../../components/Navigation';
import { messagingService } from '../../services/api/messaging.service';
import { MentionableUser } from '../../components/Chat/MessageInput';

const ChatContainer = styled('div')(({ theme }) => ({
  height: '100vh',
  width: '100%',
  maxWidth: '100vw',
  display: 'flex',
  backgroundColor: theme.palette.background.default,
  overflow: 'hidden',
  boxSizing: 'border-box',
  // Fix for mobile Safari/iPad - use smaller viewport unit
  '@media (max-width: 768px)': {
    height: '100svh', // Small viewport height - excludes browser UI
  },
  '@supports (height: 100svh)': {
    height: '100svh', // Small viewport height for better mobile support
  },
}));

const SidebarSection = styled('div')(({ theme }) => ({
  width: 320,
  flexShrink: 0,
  height: '100%', // Use 100% to fit within parent container
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '@media (max-width: 768px)': {
    display: 'none', // Hide sidebar on mobile to give more space
  },
}));

const MainSection = styled('div')(({ theme }) => ({
  flex: 1,
  height: '100%', // Use 100% instead of 100vh to fit within parent
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0, // Prevent flex item from overflowing
}));

const ChatHeader = styled(AppBar)(({ theme }) => ({
  position: 'static',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  flexShrink: 0,
}));

const ChatContent = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',
  // Ensure the content area fits properly within its parent
  minHeight: 0, // Allow flex shrinking
}));

const ChatPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [topicMessageIds, setTopicMessageIds] = useState<Set<string>>(new Set());
  const [topicEmails, setTopicEmails] = useState<any[]>([]);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);

  const { user, logout } = useAuthStore();
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  const [mentionableParticipants, setMentionableParticipants] = useState<MentionableUser[]>([]);

  const {
    conversations,
    conversationsLoaded,
    currentConversation,
    messages,
    agents,
    typingAgents,
    processingAgents,
    showHtmlRenderer,
    taskSuggestions,
    availableTeammates,
    taskSuggestionsMetadata,
    showTaskSuggestions,
    initializeSocket,
    disconnectSocket,
    loadConversations,
    loadAgents,
    loadConversation,
    createConversation,
    sendMessage,
    editMessage,
    addReaction,
    removeReaction,
    setCurrentConversation,
    toggleHtmlRenderer,
    clearTaskSuggestions,
  } = useChatStore();

  // Use the HVAC panel hook to manage panel state
  const { isOpen: hvacPanelOpen, calculationId, projectId, closePanel: closeHVACPanel, openPanel: openHVACPanel } = useHVACPanel(messages);

  // Load participants for @mention autocomplete when conversation changes
  useEffect(() => {
    if (!currentConversation?.id) {
      setMentionableParticipants([]);
      return;
    }
    messagingService.getParticipants(currentConversation.id).then(participants => {
      const currentUserId = user?.id;
      const mentionable: MentionableUser[] = participants
        .filter(p => p.user_id !== currentUserId)
        .map(p => ({
          id: p.user_id,
          name: `${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email || 'Unknown',
          email: p.email,
          type: 'human' as const,
        }));
      setMentionableParticipants(mentionable);
    }).catch(() => {
      setMentionableParticipants([]);
    });
  }, [currentConversation?.id, user?.id]);

  // Handle deep linking from Knowledge Graph
  useEffect(() => {
    const conversationId = searchParams.get('conversationId');
    const messageId = searchParams.get('messageId');

    if (conversationId && conversationId !== currentConversation?.id) {
      console.log('📍 CHAT ← GRAPH: Loading conversation from deep link', {
        conversationId,
        messageId
      });

      // Load the specified conversation
      loadConversation(conversationId).then(() => {
        // After conversation loads, highlight the target message
        if (messageId) {
          setHighlightedMessageId(messageId);

          // Scroll to message after a brief delay to allow rendering
          setTimeout(() => {
            const messageElement = document.getElementById(`message-${messageId}`);
            if (messageElement) {
              messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              console.log('📍 CHAT ← GRAPH: Scrolled to message', messageId);
            }
          }, 500);

          // Clear highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMessageId(null);
          }, 3000);
        }

        // Clear URL parameters after handling
        setSearchParams({});
      }).catch((error) => {
        console.error('📍 CHAT ← GRAPH: Failed to load conversation', error);
      });
    }
  }, [searchParams, currentConversation, loadConversation, setSearchParams]);

  // Fetch message IDs for selected topics from knowledge graph
  useEffect(() => {
    const fetchTopicMessages = async () => {
      if (selectedTopics.length === 0) {
        setTopicMessageIds(new Set());
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_CONFIG.CONVERSATION_SERVICE_URL}/api/v1/topics/messages?topicIds=${selectedTopics.join(',')}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTopicMessageIds(new Set(data.messageIds || []));
        } else {
          console.error('Failed to fetch topic messages:', response.statusText);
          setTopicMessageIds(new Set());
        }
      } catch (error) {
        console.error('Error fetching topic messages:', error);
        setTopicMessageIds(new Set());
      }
    };

    fetchTopicMessages();
  }, [selectedTopics]);

  // Fetch email details when topic filter results change
  useEffect(() => {
    const fetchEmailDetails = async () => {
      if (topicMessageIds.size === 0) {
        setTopicEmails([]);
        return;
      }

      // Extract email IDs (those starting with "email_")
      const emailIds = Array.from(topicMessageIds).filter(id => id.startsWith('email_'));

      if (emailIds.length === 0) {
        setTopicEmails([]);
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          `${API_CONFIG.CONVERSATION_SERVICE_URL}/api/v1/topics/emails`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ emailIds, scope: 'personal' })
          }
        );

        if (response.ok) {
          const data = await response.json();
          setTopicEmails(data.emails || []);
        } else {
          console.error('Failed to fetch email details:', response.statusText);
          setTopicEmails([]);
        }
      } catch (error) {
        console.error('Error fetching email details:', error);
        setTopicEmails([]);
      }
    };

    fetchEmailDetails();
  }, [topicMessageIds]);

  // Filter and combine messages with emails based on selected topics
  const filteredMessages = useMemo(() => {
    // If no topics are selected, return all messages
    if (selectedTopics.length === 0) {
      return messages;
    }

    // Filter conversation messages
    const conversationMessages = messages.filter((message) => topicMessageIds.has(message.id));

    // Convert emails to message format
    const emailMessages = topicEmails.map((email: any) => ({
      id: email.id,
      conversation_id: currentConversation?.id || '',
      user_id: user?.id || '',
      role: 'assistant' as const, // Display as assistant message
      content: `**📧 Email**\n\n**From:** ${email.from}\n**Subject:** ${email.subject}\n\n${email.snippet || email.body}`,
      created_at: email.createdAt,
      updated_at: email.createdAt,
      reactions: [],
      content_type: 'markdown' as const,
      metadata: {
        type: 'email',
        from: email.from,
        to: email.to,
        subject: email.subject
      }
    }));

    // Combine and sort by date
    const combined = [...conversationMessages, ...emailMessages].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    return combined;
  }, [messages, selectedTopics, topicMessageIds, topicEmails, currentConversation?.id, user?.id]);

  // Wrap functions that are used in useEffect dependencies
  const handleCreateConversation = useCallback(async () => {
    try {
      await createConversation();
      if (isMobile) {
        setMobileOpen(false);
      }
    } catch (err) {
      console.error('[ChatPage] Failed to create conversation:', err);
    }
  }, [createConversation, isMobile]);

  // Initialize on mount
  useEffect(() => {
    const initialize = async () => {
      const token = localStorage.getItem('authToken');

      if (token) {
        // Decode token to check its contents
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          // Check if token is expired
          if (new Date(payload.exp * 1000) < new Date()) {
            return; // Token expired, let auth handling take care of it
          }
        } catch (e) {
          console.error('[ChatPage] Failed to decode token:', e);
        }

        initializeSocket(token);

        try {
          await loadConversations();
          await loadAgents();
        } catch (err) {
          console.error('[ChatPage] Initialization error:', err);
        }
      }
    };
    
    initialize();

    return () => {
      disconnectSocket();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - functions from store are stable

  // Auto-open conversation when conversations are loaded
  useEffect(() => {
    const autoOpen = async () => {
      if (conversations.length > 0 && !currentConversation) {
        setCurrentConversation(conversations[0]);
      } else if (conversationsLoaded && conversations.length === 0 && !currentConversation && agents.length > 0) {
        // Only auto-create if conversations were successfully loaded (not on load failure)
        try {
          await handleCreateConversation();
        } catch (err) {
          console.error('[ChatPage] Failed to create conversation:', err);
        }
      }
    };

    autoOpen();
  }, [conversations, conversationsLoaded, currentConversation, handleCreateConversation, setCurrentConversation, agents.length]);

  // Load messages when conversation changes
  useEffect(() => {
    if (currentConversation) {
      // Only load if we don't already have messages for this conversation
      // or if it's a different conversation than what we last loaded
      const hasMessagesForCurrentConversation = messages.length > 0 &&
        messages[0]?.conversation_id === currentConversation.id;

      if (!hasMessagesForCurrentConversation) {
        loadConversation(currentConversation.id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversation?.id]); // Only depend on conversation ID, not the entire object

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSelectConversation = (conversation: any) => {
    setCurrentConversation(conversation);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleToggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const getCurrentUserId = () => {
    // Get user ID from auth store
    const { user } = useAuthStore.getState();
    if (user?.id) {
      return user.id;
    }
    
    // Fallback: try to get from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser?.id) {
          return parsedUser.id;
        }
      } catch (e) {
        console.error('[ChatPage] Failed to parse stored user:', e);
      }
    }
    
    // Final fallback: decode from token
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload?.sub) {
          return payload.sub;
        }
      } catch (e) {
        console.error('[ChatPage] Failed to decode token for user ID:', e);
      }
    }
    
    console.error('[ChatPage] Could not determine user ID');
    return null;
  };

  const handleConversationUpdate = (updatedConversation: Conversation) => {
    // If the updated conversation is the current one, update it in the store
    if (currentConversation?.id === updatedConversation.id) {
      setCurrentConversation(updatedConversation);
    }

    // Reload conversations to ensure consistency
    loadConversations();
  };

  const handleConversationDelete = async (conversationId: string) => {
    // If the deleted conversation is currently selected, clear it
    if (currentConversation?.id === conversationId) {
      setCurrentConversation(null);
    }
    // Reload conversations to reflect the deletion
    await loadConversations();
  };

  const handleTopicSelect = (topicId: string) => {
    setSelectedTopics((prev) => [...prev, topicId]);
  };

  const handleTopicDeselect = (topicId: string) => {
    setSelectedTopics((prev) => prev.filter((id) => id !== topicId));
  };

  const handleClearTopicFilters = () => {
    setSelectedTopics([]);
  };

  const handleViewInGraph = () => {
    if (currentConversation?.graph_node_id) {
      // Navigate to graph with conversation node focused
      navigate(`/knowledge-graph?nodeId=${currentConversation.graph_node_id}&focus=conversation`);
    } else {
      console.warn('Current conversation does not have a graph_node_id');
    }
  };

  const handleOpenPanel = (panelType: string, metadata: any) => {
    switch (panelType) {
      case 'hvac':
        if (metadata.calculationId) {
          openHVACPanel(metadata.calculationId, metadata.projectId);
        }
        break;

      case 'electrical':
        // TODO: Implement electrical panel opening
        break;

      case 'mep-coordination':
        // TODO: Implement MEP coordination panel opening
        break;

      case 'app':
        // TODO: Implement generic app panel opening
        break;

      default:
        console.warn('[ChatPage] Unknown panel type:', panelType);
    }
  };

  const sidebar = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflow: 'auto' }}>
        <ChatSidebar
          conversations={conversations}
          agents={agents}
          currentConversation={currentConversation}
          onSelectConversation={handleSelectConversation}
          onCreateConversation={handleCreateConversation}
          onConversationUpdate={handleConversationUpdate}
          onConversationDelete={handleConversationDelete}
          selectedTopics={selectedTopics}
          onTopicSelect={handleTopicSelect}
          onTopicDeselect={handleTopicDeselect}
          onClearTopicFilters={handleClearTopicFilters}
        />
      </div>
    </div>
  );

  return (
    <ChatLayout>
      <ChatContainer>
        {/* Sidebar Section - Fixed 320px width */}
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile
            }}
            PaperProps={{ style: { width: 320 } }}
          >
            {sidebar}
          </Drawer>
        ) : (
          <SidebarSection>
            {sidebar}
          </SidebarSection>
        )}

        {/* Main Chat Section - Expands to fill remaining space */}
        <MainSection>
          {/* Chat Header with app branding and controls */}
          <ChatHeader>
            <Toolbar style={{ minWidth: 0, overflow: 'hidden' }}>
              {isMobile && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleDrawerToggle}
                  style={{ marginRight: 16 }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              
              <Typography variant="h6" component="div" style={{ color: '#325BA8', fontWeight: 'bold', marginRight: 8 }}>
                Prismatic Forge
              </Typography>
              
              <Divider orientation="vertical" flexItem style={{ marginLeft: 16, marginRight: 16 }} />
              
              <Typography
                variant="h6"
                component="div"
                style={{
                  flexGrow: 1,
                  fontWeight: currentConversation ? 500 : 400,
                  color: currentConversation ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.6)',
                  fontStyle: currentConversation ? 'normal' : 'italic'
                }}
              >
                {currentConversation?.title || 'Select a conversation'}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={showHtmlRenderer}
                    onChange={toggleHtmlRenderer}
                    size="small"
                  />
                }
                label="HTML"
                style={{ marginRight: 8, flexShrink: 0 }}
              />

              {/* Mode Selector */}
              <div style={{ marginRight: 8, flexShrink: 0 }}>
                <ModeSelector size="small" showLabels={!isMobile} />
              </div>

              {/* Overflow menu for navigation actions */}
              <Tooltip title="More actions">
                <IconButton
                  color="inherit"
                  onClick={(e) => setMoreMenuAnchor(e.currentTarget)}
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={() => setMoreMenuAnchor(null)}
              >
                <MenuItem onClick={() => { navigate('/tasks'); setMoreMenuAnchor(null); }}>
                  <ListItemIcon><AssignmentIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Tasks</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { navigate('/workflows'); setMoreMenuAnchor(null); }}>
                  <ListItemIcon><WorkflowIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Workflows</ListItemText>
                </MenuItem>
                {currentConversation?.graph_node_id && (
                  <MenuItem onClick={() => { handleViewInGraph(); setMoreMenuAnchor(null); }}>
                    <ListItemIcon><GraphIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Knowledge Graph</ListItemText>
                  </MenuItem>
                )}
                <MenuItem onClick={() => { handleToggleFullscreen(); setMoreMenuAnchor(null); }}>
                  <ListItemIcon>
                    {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText>{isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}</ListItemText>
                </MenuItem>
              </Menu>

              {/* User avatar dropdown */}
              <Tooltip title={user?.email || 'Account'}>
                <IconButton
                  onClick={(e) => setAvatarMenuAnchor(e.currentTarget)}
                  style={{ marginLeft: 8 }}
                >
                  <Avatar
                    style={{
                      width: 32,
                      height: 32,
                      backgroundColor: '#325BA8',
                      fontSize: '0.875rem',
                    }}
                  >
                    {user?.email?.charAt(0).toUpperCase() || <PersonIcon />}
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={avatarMenuAnchor}
                open={Boolean(avatarMenuAnchor)}
                onClose={() => setAvatarMenuAnchor(null)}
              >
                {user && (
                  <MenuItem disabled style={{ opacity: 0.7 }}>
                    <Typography variant="body2" noWrap>{user.email}</Typography>
                  </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={() => { setSettingsOpen(true); setAvatarMenuAnchor(null); }}>
                  <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Settings</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { logout(); window.location.href = '/login'; }}>
                  <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </Toolbar>
          </ChatHeader>

          {/* Chat Content Area or HVAC Panel */}
          {hvacPanelOpen && calculationId ? (
            // Show HVAC Panel in place of chat content
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
              <HVACCalculationPanel
                calculationId={calculationId}
                projectId={projectId || undefined}
                onClose={closeHVACPanel}
                authToken={localStorage.getItem('authToken') || ''}
              />
            </div>
          ) : (
            // Show Chat Content normally
            <ChatContent>
                {currentConversation ? (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                    {/* Topic Filter Indicator */}
                    {selectedTopics.length > 0 && (
                      <div
                        style={{
                          paddingLeft: 16,
                          paddingRight: 16,
                          paddingTop: 12,
                          paddingBottom: 12,
                          borderBottom: '1px solid rgba(0,0,0,0.12)',
                          backgroundColor: '#fff',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <FilterListIcon fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Filtering by topics:
                          </Typography>
                          {selectedTopics.map((topicId) => (
                            <Chip
                              key={topicId}
                              label={topicId}
                              size="small"
                              onDelete={() => handleTopicDeselect(topicId)}
                              deleteIcon={<CloseIcon fontSize="small" />}
                              color="primary"
                              variant="outlined"
                            />
                          ))}
                          <Button
                            size="small"
                            onClick={handleClearTopicFilters}
                            startIcon={<CloseIcon fontSize="small" />}
                            style={{ marginLeft: 'auto' }}
                          >
                            Clear All
                          </Button>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" style={{ marginTop: 4, display: 'block' }}>
                          Showing {filteredMessages.length} of {messages.length} messages
                        </Typography>
                      </div>
                    )}

                    <MessageList
                      messages={filteredMessages}
                      agents={agents}
                      currentUserId={getCurrentUserId()}
                      typingAgentIds={Array.from(typingAgents)}
                      isProcessing={processingAgents.size > 0}
                      processingAgentName={processingAgents.has('system') ? undefined :
                        agents.find(a => processingAgents.has(a.id))?.custom_name ||
                        agents.find(a => processingAgents.has(a.id))?.name}
                      onEditMessage={editMessage}
                      onReactMessage={addReaction}
                      onRemoveReaction={removeReaction}
                      onOpenPanel={handleOpenPanel}
                      highlightedMessageId={highlightedMessageId}
                      conversationType={currentConversation.conversation_type || 'ai_chat'}
                    />
                    <MessageInput
                      onSendMessage={sendMessage}
                      agents={agents}
                      conversationId={currentConversation.id}
                      conversationType={currentConversation.conversation_type || 'ai_chat'}
                      participants={mentionableParticipants}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                      textAlign: 'center',
                      padding: 32,
                    }}
                  >
                    <div>
                      <Typography variant="h5" color="text.secondary" gutterBottom>
                        Welcome to Prismatic Chat
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Select a conversation from the sidebar or create a new one to get started
                      </Typography>
                    </div>
                  </div>
                )}
            </ChatContent>
          )}
        </MainSection>
        
        <SettingsDialog 
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
        />
        
        {/* Removed Drawer - HVAC Panel now replaces chat content */}
      </ChatContainer>

      {/* Task Suggestions Split View */}
      <TaskSuggestionsSplitView
        open={showTaskSuggestions}
        onClose={clearTaskSuggestions}
        tasks={taskSuggestions}
        availableTeammates={availableTeammates}
        conversationId={currentConversation?.id || ''}
        sourceAgent={taskSuggestionsMetadata?.sourceAgent}
        sourceMessage={messages.length > 0 ? messages[messages.length - 1] : undefined}
      />
    </ChatLayout>
  );
};

export default ChatPage;